'use client';

/**
 * `useLearningJourney`, top-level React bridge for the learning-journey
 * runtime. Couples the pure-TS `LearningJourney` (lib) to the live
 * `Studio` (audio/transport/piano) and exposes a stable React snapshot.
 *
 * Responsibilities (one hook, one route, `/learn-piece/[songId]`):
 *
 *   1. Build a `LearningJourney` once the `Song` is available and the
 *      `Studio` is ready. The phrase plan is computed deterministically
 *      from the song; its hash drives persistence invalidation
 *      (cf. `progress-store.ts`).
 *   2. Build a `RuntimeBridge` adapter over the `Studio`, then a
 *      `RangeRunner` over that adapter, handed back to the caller so
 *      the UI states (S1b/S3/S4/S5) can drive playback runs.
 *   3. Wire piano events and metronome beats into the journey via
 *      dedicated bridge hooks (timestamping at `performance.now()` for
 *      piano events, mapping the metronome beat to a tick at fire time).
 *   4. Mirror the journey snapshot into React state so consumers re-render
 *      on FSM transitions (capture writes are silent, see `journey.ts`).
 *   5. Publish piano-roll hints driven by the snapshot (`useJourneyHints`).
 *      S4/S5 produce no hints by spec.
 *
 * Cleanup
 * -------
 * Each side-effect hook owns its teardown:
 *
 *   - `useJourneyPianoBridge` unsubscribes from `Piano.on`.
 *   - `useJourneyMetronomeBridge` unsubscribes from `ContinuousMetronome.onBeat`.
 *   - The journey-subscription effect detaches its listener.
 *   - The hint hook retracts its slot via `useHintPublisher`.
 *
 * We intentionally do *not* call `journey.reset()` on unmount: the player
 * leaves the page with their progression intact (persisted via
 * `ProgressStore.save`). Resetting would discard hard-won progress on a
 * route change.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useStudio } from '@/contexts/studio-context';
import {
  LearningJourney,
  type JourneySnapshot,
} from '@/lib/learning-journey/journey';
import {
  buildPhrasesFromSong,
  hashPhrasePlan,
  type SongLike,
} from '@/lib/learning-journey/phrase-plan';
import {
  LocalStorageProgressStore,
  type ProgressStore,
} from '@/lib/learning-journey/progress-store';
import { SyncingProgressStore } from '@/lib/learning-journey/syncing-progress-store';
import { RangeRunner } from '@/lib/learning-journey/runtime/range-runner';
import { createStudioRuntimeBridge } from './studio-runtime-bridge';
import { useJourneyAnalytics } from './use-journey-analytics';
import { useJourneyHints } from './use-journey-hints';
import { useJourneyMetronomeBridge } from './use-journey-metronome-bridge';
import { useJourneyPianoBridge } from './use-journey-piano-bridge';

export type UseLearningJourneyOptions = {
  /** The loaded song. Pass `null` while it's still being fetched/parsed. */
  readonly song: SongLike | null;
  /** Stable identifier for the song, drives persistence keys. */
  readonly morceauId: string;
  /**
   * L'identité **catalogue** du morceau, `null` quand il n'en vient pas.
   *
   * Elle ne sert qu'à la mesure et ne touche à rien d'autre : `morceauId` est
   * la clé de progression (`sourceMidiId`, éventuellement suffixé `:t<n>` ou
   * `:n<rungId>`), donc un identifiant de **fichier sous réglage**, quand
   * `pieceId` est celui que `piece_opened` et `studio_session_ended` emploient.
   * Les confondre rendait zéro ligne à toute jointure entre les deux.
   */
  readonly pieceId?: string | null;
  /**
   * Optional override. Defaults to a `LocalStorageProgressStore` backed
   * by `window.localStorage`. Tests / dev tools can pass a
   * `MemoryProgressStore`.
   */
  readonly progressStore?: ProgressStore;
};

export type UseLearningJourneyResult = {
  readonly journey: LearningJourney | null;
  readonly snapshot: JourneySnapshot | null;
  readonly rangeRunner: RangeRunner | null;
};

export function useLearningJourney(
  opts: UseLearningJourneyOptions,
): UseLearningJourneyResult {
  const { song, morceauId, pieceId = null, progressStore: providedStore } = opts;
  const { studio } = useStudio();

  // Default progress store. Built lazily once and pinned for the hook's
  // lifetime so the journey instance stays stable across re-renders.
  const defaultStoreRef = useRef<ProgressStore | null>(null);

  const progressStore = useMemo<ProgressStore | null>(() => {
    if (providedStore !== undefined) {
      return providedStore;
    }

    // SSR / test-node guard: `LocalStorageProgressStore` throws when no
    // Storage is available. We defer the wiring to the client by returning
    // `null` here; the journey-construction effect then bails until we
    // have a real store at hand.
    if (typeof window === 'undefined' || typeof window.localStorage === 'undefined') {
      return null;
    }

    if (defaultStoreRef.current === null) {
      // Le magasin local reste la source : `load` est synchrone, et le parcours
      // le lit au moment de construire son état. Le décorateur ne fait
      // qu'ajouter une poussée vers le serveur, sans l'attendre — c'est ce qui
      // permet de reprendre sur un autre appareil sans mettre le réseau dans le
      // chemin critique du jeu.
      defaultStoreRef.current = new SyncingProgressStore(
        new LocalStorageProgressStore(window.localStorage),
      );
    }

    return defaultStoreRef.current;
  }, [providedStore]);

  const [journey, setJourney] = useState<LearningJourney | null>(null);
  const [snapshot, setSnapshot] = useState<JourneySnapshot | null>(null);
  const [rangeRunner, setRangeRunner] = useState<RangeRunner | null>(null);

  // Build / rebuild the journey when song or studio change.
  useEffect(() => {
    if (song === null || studio === null || progressStore === null) {
      setJourney(null);
      setSnapshot(null);
      setRangeRunner(null);

      return;
    }

    const phrases = buildPhrasesFromSong(song);
    const phrasePlanHash = hashPhrasePlan(phrases);

    const instance = new LearningJourney({
      morceauId,
      phrases,
      phrasePlanHash,
      progressStore,
    });

    const bridge = createStudioRuntimeBridge(studio);
    const runner = new RangeRunner(bridge);

    setJourney(instance);
    setSnapshot(instance.getSnapshot());
    setRangeRunner(runner);

    // The bridge holds no state of its own; nothing to dispose besides
    // dropping the reference. The runner releases its tick subscription
    // through `cancel()` if a run is still in flight.
    return () => {
      runner.cancel();
      setJourney(null);
      setSnapshot(null);
      setRangeRunner(null);
    };
  }, [song, studio, morceauId, progressStore]);

  // Subscribe to journey snapshots and mirror them into React state.
  useEffect(() => {
    if (journey === null) {
      return;
    }

    setSnapshot(journey.getSnapshot());

    const unsubscribe = journey.subscribe((next) => {
      setSnapshot(next);
    });

    return () => {
      unsubscribe();
    };
  }, [journey]);

  // Forward piano + metronome events into the journey.
  useJourneyPianoBridge(journey, studio);
  useJourneyMetronomeBridge(journey, studio);

  // Publish piano-roll hints derived from the current snapshot. Spec:
  // S4/S5 produce no hints, the helper handles that.
  useJourneyHints(snapshot);

  // Observe the same snapshots for product analytics. Kept outside
  // `LearningJourney` so the domain class stays free of DOM/network imports.
  // Les deux identifiants descendent : `morceauId` borne la session (c'est
  // lui qui change quand on transpose), `pieceId` est le seul qui parte dans
  // les payloads.
  useJourneyAnalytics({ journey, snapshot, morceauId, pieceId });

  return useMemo(
    () => ({ journey, snapshot, rangeRunner }),
    [journey, snapshot, rangeRunner],
  );
}
